import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Form, Input } from "reactstrap"

import LoadingButton from "../../components/Common/LoadingButton"
import AuthLayout from "../../components/Common/AuthLayout"

import { useLocation } from "react-router-dom"
import withRouter from "../../components/Common/withRouter"

import { useDispatch } from "react-redux"
import { toast, Bounce } from "react-toastify"
import { post } from "../../api/axios"
import { toastApiError } from "../../helpers/apiError"
import endpoints from "../../api/endpoints"
import { setLogin } from "../../store/slices/authSlice"
import { getAuthenticatedHomePath } from "../../helpers/routeAccess"
import * as Yup from "yup"
import { useFormik } from "formik"

const RESEND_COOLDOWN_SECONDS = 60

const VerifyOtp = props => {
  document.title = "Verify OTP | AMOK . EARTH"
  const location = useLocation()
  const state = location.state
  const dispatch = useDispatch()
  const navigate = props.router?.navigate
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
  const [isResending, setIsResending] = useState(false)
  const cooldownTimerRef = useRef(null)
  const inputRefs = [
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]

  useEffect(() => {
    if (state?.email) return
    navigate("/login", { replace: true })
  }, [navigate, state?.email])

  const clearCooldownTimer = useCallback(() => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current)
      cooldownTimerRef.current = null
    }
  }, [])

  const startResendCooldown = useCallback(() => {
    clearCooldownTimer()
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current)
          cooldownTimerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [clearCooldownTimer])

  useEffect(() => {
    startResendCooldown()
    return clearCooldownTimer
  }, [startResendCooldown, clearCooldownTimer])

  const canResend = resendCooldown === 0 && !isResending

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1)
    const fieldName = index === 0 ? "code" : `code${index}`
    validation.setFieldValue(fieldName, digit)

    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus()
    }
  }

  const handleOtpPaste = e => {
    e.preventDefault()
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, 4)
    for (let i = 0; i < pastedData.length; i += 1) {
      const fieldName = i === 0 ? "code" : `code${i}`
      validation.setFieldValue(fieldName, pastedData[i])
      validation.setFieldTouched(fieldName, true)
    }
    const nextIndex = Math.min(pastedData.length, 3)
    inputRefs[nextIndex].current?.focus()
  }

  const handleOtpBlur = index => {
    const fieldName = index === 0 ? "code" : `code${index}`
    validation.setFieldTouched(fieldName, true)
  }

  const handleResend = async () => {
    if (!canResend) return

    const email = state?.email || localStorage.getItem("reset_email")
    if (!email) return

    setIsResending(true)
    startResendCooldown()

    try {
      const response = await post(endpoints.resendVerification, {
        email,
        type: state?.otpType,
      })
      if (response?.data?.success) {
        toast.success(response?.data?.message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
      } else {
        toastApiError(response, "Failed to resend code")
      }
    } catch (error) {
      toastApiError(error, "Failed to resend code")
    } finally {
      setIsResending(false)
    }
  }

  const handleKeyDown = (index, e) => {
    const fieldName = index === 0 ? "code" : `code${index}`
    if (e.key === "Backspace" && !validation.values[fieldName] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      code: "",
      code1: "",
      code2: "",
      code3: "",
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .required("Please enter your code")
        .matches(/^\d$/, "Please enter a valid digit"),
      code1: Yup.string()
        .required("Please enter your code")
        .matches(/^\d$/, "Please enter a valid digit"),
      code2: Yup.string()
        .required("Please enter your code")
        .matches(/^\d$/, "Please enter a valid digit"),
      code3: Yup.string()
        .required("Please enter your code")
        .matches(/^\d$/, "Please enter a valid digit"),
    }),
    onSubmit: async values => {
      const fcmToken = localStorage.getItem("fcm")
      const email = state?.email || localStorage.getItem("reset_email")
      const otp = `${values.code}${values.code1}${values.code2}${values.code3}`
      const type = state?.otpType

      if (isSubmitting) return
      setIsSubmitting(true)
      try {
        const response = await post(endpoints.verifyByOtp, {
          email,
          otp,
          type,
          fcm_token: fcmToken,
          device_id: fcmToken,
        })

        if (response?.data?.success) {
          const result = response?.data?.result

          if (result?.token) {
            localStorage.setItem("auth_token", result.token)
          }
          if (result) {
            localStorage.setItem("user_data", JSON.stringify(result))
          }
          if (email) {
            localStorage.setItem("reset_email", email)
          }
          localStorage.setItem("otp_type", type)

          toast.success(response?.data?.message, {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          })

          if (type === "email-verification") {
            navigate("/login")
          } else if (type === "forgot-password") {
            navigate("/reset-password", {
              state: { email, userData: result },
            })
          } else if (type === "2fa-verification") {
            const user = result?.user || state?.pendingUser || result
            const token = result?.token || state?.pendingToken
            if (!token) {
              toastApiError(
                response,
                "Verification succeeded but no session token was returned.",
              )
              return
            }
            dispatch(
              setLogin({
                currentUser: user,
                jid: token,
                isAuth: true,
                loginType: "credentials",
              }),
            )
            navigate(getAuthenticatedHomePath(user), { replace: true })
          } else {
            if (result?.token) {
              dispatch(
                setLogin({
                  currentUser: result?.user || result,
                  jid: result.token,
                  isAuth: true,
                  loginType: "credentials",
                }),
              )
            }
            const user = result?.user || result
            navigate(getAuthenticatedHomePath(user), { replace: true })
          }
        } else if (response?.response?.data) {
          toastApiError(response)
        }
      } catch (error) {
        toastApiError(error, "Verification failed. Please try again.")
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const hasOtpError =
    (validation.touched.code && validation.errors.code) ||
    (validation.touched.code1 && validation.errors.code1) ||
    (validation.touched.code2 && validation.errors.code2) ||
    (validation.touched.code3 && validation.errors.code3)

  return (
    <AuthLayout
      title={
        state?.otpType === "email-verification"
          ? "Email Verification"
          : "Verify OTP"
      }
      subtitle={
        state?.otpType === "email-verification" ? (
          <>
            We have sent a verification link to your email address.
            <br />
            Please check your inbox.
          </>
        ) : (
          <div className="lineHeight">
            We’ve sent a 4-digit verification code to
            <br />
            <b className="two-email">{state?.email}</b>
            <br />
            Please check your inbox.
          </div>
        )
      }
    >
      <Form
        className="auth-page__form"
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <div className="auth-page__otp-group">
          <p className="auth-page__otp-label">Verification Code</p>

          <div className="auth-page__otp">
            {[0, 1, 2, 3].map(index => {
              const name = index === 0 ? "code" : `code${index}`
              const isInvalid = !!(
                validation.touched[name] && validation.errors[name]
              )
              return (
                <Input
                  key={name}
                  name={name}
                  className="form-control"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength="1"
                  value={validation.values[name]}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onPaste={handleOtpPaste}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onBlur={() => handleOtpBlur(index)}
                  innerRef={inputRefs[index]}
                  invalid={isInvalid}
                />
              )
            })}
          </div>

          {hasOtpError ? (
            <p className="auth-page__otp-error">
              Please enter the complete verification code
            </p>
          ) : null}
        </div>

        <LoadingButton loading={isSubmitting}>Verify</LoadingButton>

        <p className="auth-page__divider">Or</p>

        <p className="auth-page__footer-text auth-page__footer-text--muted">
          <span>Didn&apos;t receive the code?</span>{" "}
          <span
            className={`auth-page__resend${canResend ? "" : " is-disabled"}`}
            onClick={canResend ? handleResend : undefined}
            onKeyDown={e => {
              if (!canResend) return
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleResend()
              }
            }}
            role="button"
            tabIndex={canResend ? 0 : -1}
            aria-disabled={!canResend}
          >
            {canResend ? "Resend" : `Resend in ${resendCooldown}s`}
          </span>
        </p>
        <p className="auth-page__footer-text auth-page__footer-text--muted">
          <span>Wrong email?</span>{" "}
          <span
            className={`auth-page__resend`}
            onClick={() => navigate("/forgot-password")}
            role="button"
            tabIndex={0}
            aria-disabled={false}
          >
            Change Email
          </span>
        </p>
      </Form>
    </AuthLayout>
  )
}

export default withRouter(VerifyOtp)

VerifyOtp.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
}
